import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {AsyncStatusesEnum} from '../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {isTeammateNamespace} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {DropdownBoxCoordinator} from '../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {DropdownLoader} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownLoader';
import {SearchDropdown} from '../../../../libs/shared/ui/src/components/dropdowns/search/searchDropdown';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {useAppDispatch, useAppSelector} from '../../../helpers/appReduxHelpers';
import {getAnalyticsSelectedNamespace} from '../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {fetchSavedViewsAsyncAction} from '../../../store/panels/analytics/views/analyticsPanelSavedViewsActions';
import {
  getAnalyticsAllViews,
  getAnalyticsViewResourceMaps,
} from '../../../store/panels/analytics/views/analyticsPanelViewsSelectors';
import {
  AnalyticsSavedView,
  isAnalyticsViewTemporary,
} from '../../../store/panels/analytics/views/analyticsPanelViewsState';
import {makeFilterRows} from '../../helpers/analyticsFiltersHelpers';
import {SavedViewsDropdownItem} from '../savedViewsDropdown/savedViewsDropdownItem';
import {renderSupportReportMetricView} from './supportReportConfigurationHelpers';

/*
 * Props.
 */

interface SupportReportConfigurationViewDropdownProps {
  selectedViewId?: number;
  onViewSelect: (viewId: number) => void;
}

/*
 * Style.
 */

const StyledPlaceholderDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
`;

/*
 * Component.
 */

export const SupportReportConfigurationViewDropdown: FC<SupportReportConfigurationViewDropdownProps> = ({
  selectedViewId,
  onViewSelect,
}) => {
  const {views} = useAppSelector(getAnalyticsAllViews);
  const [query, setQuery] = useState('');
  const dispatch = useAppDispatch();

  const {areAllSavedViewsLoaded, isFetchingSavedViews} = useAppSelector(getAnalyticsAllViews);
  const selectedView = views.find((view) => view.id === selectedViewId);
  const data = useMemo(
    () =>
      views.filter(
        (view) =>
          !isAnalyticsViewTemporary(view.id) && view.namespace && !isTeammateNamespace(view.namespace),
      ),
    [views],
  );
  const filteredData = useMemo(() => {
    if (query) {
      return filterRows(data, query);
    }
    return data;
  }, [data, query]);

  const selectedNamespace = useAppSelector(getAnalyticsSelectedNamespace);
  const resourceMaps = useAppSelector(getAnalyticsViewResourceMaps);

  const onFetchSavedViews = () => {
    if (!areAllSavedViewsLoaded && !isFetchingSavedViews) {
      dispatch(fetchSavedViewsAsyncAction(selectedNamespace));
    }
  };

  const renderRow = (item: AnalyticsSavedView) => (
    <SavedViewsDropdownItem
      item={item}
      resourceMaps={resourceMaps}
      onSelect={() => {
        if (typeof item.id !== 'number') {
          return;
        }
        onViewSelect(item.id);
      }}
      isPinDisabled
      isCheckboxDisabled
      isChecked={item.id === selectedViewId}
    />
  );

  const maybeRenderLoading = () => {
    if (!areAllSavedViewsLoaded) {
      return (
        <DropdownLoader
          size={VisualSizesEnum.MEDIUM}
          status={AsyncStatusesEnum.LOADING}
          onRetryClick={onFetchSavedViews}
        />
      );
    }

    return null;
  };

  return (
    <DropdownBoxCoordinator
      value={
        selectedView ? (
          renderSupportReportMetricView(selectedView)
        ) : (
          <StyledPlaceholderDiv>
            <Trans id="analytics-automation-reports-support-report-placeholder">Select a view</Trans>
          </StyledPlaceholderDiv>
        )
      }
      label={<Trans id="analytics-automation-reports-support-report-view-input">View</Trans>}
      additionalLabelInfo={
        <TooltipCoordinator
          isInline
          placement="top"
          render={() => (
            <Trans id="analytics-automation-reports-support-report-tooltip">
              Controls what data is used to generate the metric.
            </Trans>
          )}
        />
      }
      onPopoverToggle={onFetchSavedViews}
      isRequired
      hasError={Boolean(selectedViewId && !selectedView)}
      errorMessage={
        <Trans id="analytics-automation-reports-support-report-view-error-message">
          The selected view is not available to you or has been deleted.
        </Trans>
      }
      renderDropdown={(popoverProps) => (
        <SearchDropdown
          {...popoverProps}
          query={query}
          onQueryChange={setQuery}
          renderOverlay={maybeRenderLoading}
          data={filteredData}
          findRowHeight={() => 56}
          renderRow={renderRow}
          width={300}
          headerTitle={
            <Trans id="analytics-automation-reports-support-report-view-select-title">All views</Trans>
          }
        />
      )}
    />
  );
};

/*
 * Helpers.
 */

const filterRows = makeFilterRows<AnalyticsSavedView>((item) => item.name);
